@if (contentLoaded) {
  <div class="modal-header">
    <h4 class="modal-title" id="modal-basic-title">Create New Register</h4>

    <button
      type="button"
      class="btn-close"
      aria-label="Close"
      (click)="modal.dismiss('exit')"
    ></button>
  </div>

  <form [formGroup]="form" (validSubmit)="save($event)">
    <div class="modal-body form-section">
      <app-subscription-limit
        [addingRegistersCount]="1"
        [addingAssetsCount]="0"
      >
      </app-subscription-limit>

      @if (canAddRegister) {
        <app-form-control [columnLayout]="true" size="medium" margin="small">
          <app-form-label [required]="true">Register Name</app-form-label>
          <input
            type="text"
            appFormControl
            formControlName="name"
            ngbAutofocus
          />
          @if (form.controls.name.hasError('required')) {
            <div class="invalid-feedback">Register Name is required</div>
          }
        </app-form-control>

        <app-form-control [columnLayout]="true" size="medium" margin="small">
          <app-form-label [required]="true">Entity Name</app-form-label>
          <input type="text" appFormControl formControlName="entityName" />
          @if (form.controls.entityName.hasError('required')) {
            <div class="invalid-feedback">Entity Name is required</div>
          }
        </app-form-control>

        <app-form-control [columnLayout]="true" size="small" margin="small">
          <app-form-label [required]="true"
            >Country / Jurisdiction</app-form-label
          >
          <select
            appFormControl
            formControlName="countryId"
            (ngModelChange)="changeCountry($event)"
          >
            @for (country of countries; track country) {
              <option [ngValue]="country.id">
                {{ country.name }}
              </option>
            }
          </select>
        </app-form-control>

        <app-form-control [columnLayout]="true" size="small" margin="small">
          <app-form-label [required]="true">Entity Type</app-form-label>
          <select appFormControl formControlName="entityTypeId">
            @for (entityType of entityTypes; track entityType) {
              <option [value]="entityType.id">
                {{ entityType.name }}
              </option>
            }
          </select>
        </app-form-control>

        <app-form-control [columnLayout]="true" size="small" margin="small">
          <app-form-label> Currency </app-form-label>
          <select appFormControl formControlName="currency">
            <option value="">
              @if (!country || country.currency) {
                Default
                @if (country?.currency; as currency) {
                  ({{ currency }})
                }
              } @else {
                Select&hellip;
              }
            </option>
            @for (currency of CURRENCIES; track currency) {
              <option [ngValue]="currency.code">
                {{ currency.code }} - {{ currency.name }}
              </option>
            }
          </select>
          @if (form.controls.currency.hasError('required')) {
            <div class="invalid-feedback">
              Currency is required for the selected country
            </div>
          }
        </app-form-control>

        <app-form-control [columnLayout]="true" size="small" margin="small">
          <app-form-label [required]="true"
            >Reporting Year Dates</app-form-label
          >
          <select appFormControl formControlName="reportingYearDate">
            @for (
              reportingYearDate of reportingYearDates;
              track reportingYearDate
            ) {
              <option [ngValue]="reportingYearDate">
                {{ reportingYearDate?.description }}
              </option>
            }
          </select>
        </app-form-control>

        <div [hidden]="isFirstRegister" class="mt-4 mb-3">
          <input
            class="switch-input"
            type="checkbox"
            id="showSetupGuide"
            formControlName="showSetupGuide"
          />
          <label for="showSetupGuide" class="switch"></label>
          <span class="ms-2">Show Setup Guide</span>
        </div>

        <app-form-control [columnLayout]="true" size="medium" margin="small">
          <div>
            <button
              type="button"
              class="btn btn-link ps-0"
              (click)="showAdvancedSettings = !showAdvancedSettings"
            >
              {{ showAdvancedSettings ? 'Hide' : 'Show' }} advanced settings
              <i-lucide
                [name]="showAdvancedSettings ? 'chevron-up' : 'chevron-down'"
              ></i-lucide>
            </button>
          </div>
        </app-form-control>

        @if (showAdvancedSettings) {
          <div class="pt-2 advanced-settings">
            <app-form-control [columnLayout]="true" size="full" margin="small">
              <app-form-label> Accounting Standard </app-form-label>
              <div>
                <select appFormControl formControlName="accountingStandardId">
                  <option value="">
                    Follow country standard
                    @if (defaultAccountingStandard) {
                      ({{ defaultAccountingStandard.abbreviation }})
                    }
                  </option>
                  @for (
                    accountingStandard of accountingStandards;
                    track accountingStandard
                  ) {
                    <option [ngValue]="accountingStandard.id">
                      {{ accountingStandard.abbreviation }} &ndash;
                      {{ accountingStandard.name }}
                    </option>
                  }
                </select>

                <!-- When updating also update in register settings -->
                @for (
                  accountingStandard of accountingStandards;
                  track accountingStandard
                ) {
                  <app-alert
                    [hidden]="
                      accountingStandard.id !==
                      (form.value.accountingStandardId ||
                        defaultAccountingStandard?.id)
                    "
                    type="info"
                    size="small"
                    class="mt-3"
                  >
                    For
                    <abbr [attr.title]="accountingStandard.name">{{
                      accountingStandard.abbreviation
                    }}</abbr>
                    these rules apply:
                    <ul>
                      @if (accountingStandard.revaluationsAllowed) {
                        <li>Assets can be revalued up and down.</li>
                      }
                      @if (!accountingStandard.revaluationsAllowed) {
                        <li>Assets cannot be revalued.</li>
                      }
                      @if (accountingStandard.impairmentReversalsAllowed) {
                        <li>Assets can be impaired and unimpaired.</li>
                      }
                      @if (!accountingStandard.impairmentReversalsAllowed) {
                        <li>
                          Assets can be impaired but cannot be unimpaired.
                        </li>
                      }
                      @if (
                        accountingStandard.revaluationsAllowed &&
                        accountingStandard.revaluationsAreGrouped
                      ) {
                        <li>
                          Revaluation Reserve/Loss is calculated at an Asset
                          Group level when journaling.
                        </li>
                      }
                      @if (
                        accountingStandard.revaluationsAllowed &&
                        !accountingStandard.revaluationsAreGrouped
                      ) {
                        <li>
                          Revaluation Reserve/Loss is calculated at an
                          individual Asset level when journaling.
                        </li>
                      }
                    </ul>
                  </app-alert>
                }
              </div>
            </app-form-control>

            <app-form-control [columnLayout]="true" size="full" margin="small">
              <app-form-label [required]="true"
                >Accounts Depreciation</app-form-label
              >
              <div>
                <label class="form-check pt-1">
                  <input
                    type="radio"
                    class="form-check-input"
                    id="accountsDepreciation_standard"
                    name="accountsPeriodDepreciationCalculation"
                    formControlName="accountsPeriodDepreciationCalculation"
                    [value]="1"
                  />
                  <div class="form-check-label">
                    Calculated daily (recommended)
                    <div class="text-body-secondary">
                      e.g. January accounts for 31/365th (or 31/366th) of the
                      total depreciation for the year
                    </div>
                  </div>
                </label>
                <label class="form-check pt-1">
                  <input
                    type="radio"
                    class="form-check-input"
                    id="accountsDepreciation_monthly"
                    name="accountsPeriodDepreciationCalculation"
                    formControlName="accountsPeriodDepreciationCalculation"
                    [value]="0"
                  />
                  <div class="form-check-label">
                    Calculated monthly
                    <div class="text-body-secondary">
                      e.g. January accounts for 1/12th of the total depreciation
                      for the year
                    </div>
                  </div>
                </label>
              </div>
            </app-form-control>

            <app-form-control [columnLayout]="true" size="full" margin="small">
              <app-form-label>
                <label> Depreciate beyond residual </label>
              </app-form-label>
              <div class="checkbox-enhanced">
                <input
                  type="checkbox"
                  id="depreciateBeyondResidual"
                  formControlName="depreciateBeyondResidual"
                />
                <label for="depreciateBeyondResidual">
                  Assets will depreciate until they reach zero instead of when
                  they reach residual value.
                  <span class="d-block text-body-secondary mt-1">
                    Applies to straight line methods. Residual value is still
                    used in calculating depreciation.
                  </span>
                </label>
              </div>
              <!-- <label>
            <div class="text-body-secondary mt-1">
              Applies to straight line methods. Assets will depreciate until
              they reach zero instead of when they reach residual value.
              Residual value is still used in calculating depreciation.
            </div>
          </label> -->
            </app-form-control>
          </div>
        }
      }
    </div>

    @if (canAddRegister) {
      <div class="modal-footer">
        <button
          type="button"
          class="btn btn-outline"
          [disabled]="form.disabled"
          (click)="modal.dismiss()"
        >
          Cancel
        </button>

        <button
          type="submit"
          class="btn btn-primary ms-2"
          [disabled]="form.disabled"
        >
          @if (isFirstRegister) {
            <i-lucide name="play"></i-lucide>
            Next
          } @else {
            <i-lucide name="save"></i-lucide>
            Save
          }
        </button>
      </div>
    }
  </form>
}
